import { useRef, useCallback, useEffect } from "react";
import axios from "axios";
import posthog from "posthog-js";
import { API_URL } from "../config";
import { getLoadIdentifier } from "../domain/load";

// Minimum time (in ms) user must view a load before tracking
const VIEW_THRESHOLD_MS = 5000; // 5 seconds

/**
 * Hook to track when users view load details with time-based threshold.
 * Only tracks the view if the drawer was open for at least VIEW_THRESHOLD_MS.
 * Sends the event when the drawer closes, capturing the actual view duration.
 *
 * @param {Object} options
 * @param {Object} options.load - The load being viewed
 * @param {boolean} options.isOpen - Whether the drawer/modal is open
 * @param {string} options.userEmail - Current user's email (for PostHog distinct_id)
 * @returns {Object} { isTracking }
 */
export const useLoadViewTracking = ({ load, isOpen, userEmail }) => {
  const startTimeRef = useRef(null);
  const loadSnapshotRef = useRef(null);
  const trackedLoadRef = useRef(null);

  // Send tracking event to backend (fire-and-forget)
  const sendTrackingEvent = useCallback(
    (loadData, viewDurationMs) => {
      if (!loadData || !userEmail) return;

      const loadId = getLoadIdentifier(loadData);

      // Don't track the same load twice in the same session
      if (trackedLoadRef.current === loadId) {
        return;
      }

      // Mark this load as tracked immediately to prevent duplicates
      trackedLoadRef.current = loadId;

      // Fire-and-forget API call
      axios
        .post(`${API_URL}/loads/viewed`, {
          load_id: loadId,
          entry_id: loadData.entry_id,
          origin_city: loadData.origin_city,
          origin_state: loadData.origin_state,
          destination_city: loadData.destination_city,
          destination_state: loadData.destination_state,
          payment: loadData.payment,
          rate_per_mile_est: loadData.rate_per_mile_est,
          distance: loadData.distance,
          dead_head: loadData.dead_head || loadData.origin_distance,
          weight: loadData.weight,
          length: loadData.length,
          equipment_type: loadData.equipment_type,
          company_name: loadData.company_name,
          company_mc: loadData.company_mc,
          company_dot: loadData.company_dot,
          data_source: loadData.data_source,
          view_duration_ms: viewDurationMs,
          ship_date: loadData.ship_date,
        })
        .then(() => {
          console.log(
            `[LoadViewTracking] Tracked view for load: ${loadId}, duration: ${viewDurationMs}ms`
          );
        })
        .catch((error) => {
          console.error("[LoadViewTracking] Failed to track load view:", error);
        });

      // Also send to PostHog client-side for real-time dashboard updates
      posthog.capture("load_details_viewed", {
        load_id: loadId,
        origin: `${loadData.origin_city}, ${loadData.origin_state}`,
        destination: `${loadData.destination_city || ""}, ${loadData.destination_state || ""}`.trim(),
        payment: loadData.payment,
        rate_per_mile: loadData.rate_per_mile_est,
        distance: loadData.distance,
        equipment_type: loadData.equipment_type,
        company_name: loadData.company_name,
        data_source: loadData.data_source,
        view_duration_ms: viewDurationMs,
      });
    },
    [userEmail]
  );

  // Track drawer open/close and send event on close if threshold met
  useEffect(() => {
    if (isOpen && load) {
      // Drawer opened - start tracking time and snapshot the load data
      startTimeRef.current = Date.now();
      loadSnapshotRef.current = load;
    } else if (!isOpen && startTimeRef.current && loadSnapshotRef.current) {
      // Drawer closed - check if threshold was met
      const viewDuration = Date.now() - startTimeRef.current;

      if (viewDuration >= VIEW_THRESHOLD_MS) {
        // Threshold met - send tracking event with actual duration
        sendTrackingEvent(loadSnapshotRef.current, viewDuration);
      }

      // Reset refs
      startTimeRef.current = null;
      loadSnapshotRef.current = null;
    }
  }, [isOpen, load, sendTrackingEvent]);

  // Reset tracked load when a different load is selected
  useEffect(() => {
    const currentLoadId = load ? getLoadIdentifier(load) : null;
    if (currentLoadId && trackedLoadRef.current !== currentLoadId) {
      trackedLoadRef.current = null;
    }
  }, [load]);

  return {
    isTracking: startTimeRef.current !== null,
  };
};
